import { useMemo, useRef, useState } from "react";
import { Search, ArrowUpDown, ArrowUp, ArrowDown } from "lucide-react";

export default function DataTable({ columns, data, searchKeys = [], onRowClick, testid = "table", emptyText = "Aucune donnée.", cardRender = null }) {
  const [q, setQ] = useState("");
  const [sort, setSort] = useState({ key: null, dir: 1 });
  const [filters, setFilters] = useState({});
  const [widths, setWidths] = useState(() => Object.fromEntries(columns.map((c) => [c.key, c.width || null])));
  const dragRef = useRef(null);

  const filtered = useMemo(() => {
    let rows = data || [];
    if (q) {
      const t = q.toLowerCase();
      rows = rows.filter((r) => searchKeys.some((k) => String(r[k] ?? "").toLowerCase().includes(t)));
    }
    Object.entries(filters).forEach(([k, v]) => {
      if (v && v !== "__all__") rows = rows.filter((r) => String(r[k]) === v);
    });
    if (sort.key) {
      const col = columns.find((c) => c.key === sort.key);
      rows = [...rows].sort((a, b) => {
        const va = col?.sortValue ? col.sortValue(a) : a[sort.key];
        const vb = col?.sortValue ? col.sortValue(b) : b[sort.key];
        if (va == null) return 1;
        if (vb == null) return -1;
        return (va > vb ? 1 : va < vb ? -1 : 0) * sort.dir;
      });
    }
    return rows;
  }, [data, q, filters, sort, columns, searchKeys]);

  const toggleSort = (key) => setSort((s) => (s.key === key ? { key, dir: -s.dir } : { key, dir: 1 }));

  const startResize = (e, key) => {
    e.stopPropagation();
    const startX = e.clientX;
    const th = e.target.closest("th");
    const startW = th.offsetWidth;
    const move = (ev) => setWidths((w) => ({ ...w, [key]: Math.max(60, startW + ev.clientX - startX) }));
    const up = () => { document.removeEventListener("mousemove", move); document.removeEventListener("mouseup", up); };
    document.addEventListener("mousemove", move);
    document.addEventListener("mouseup", up);
  };

  const hasFilters = columns.some((c) => c.filter);

  return (
    <div data-testid={testid} className={cardRender ? "has-cards" : ""}>
      {(searchKeys.length > 0 || hasFilters) && (
        <div className="table-toolbar">
          {searchKeys.length > 0 && (
            <div className="search" style={{ position: "relative" }}>
              <Search size={14} style={{ position: "absolute", left: 10, top: 11, color: "var(--text-muted)" }} />
              <input data-testid={`${testid}-search`} placeholder="Rechercher…" value={q}
                onChange={(e) => setQ(e.target.value)} style={{ paddingLeft: 30 }} />
            </div>
          )}
          {columns.filter((c) => c.filter).map((c) => (
            <select key={c.key} data-testid={`${testid}-filter-${c.key}`} style={{ maxWidth: 200 }}
              value={filters[c.key] || "__all__"} onChange={(e) => setFilters((f) => ({ ...f, [c.key]: e.target.value }))}>
              <option value="__all__">{c.label} : tous</option>
              {c.filter.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
            </select>
          ))}
        </div>
      )}
      <div className="tablewrap">
        <table>
          <thead>
            <tr>
              {columns.map((c) => (
                <th key={c.key} className={c.sortable !== false ? "sortable" : ""}
                  style={{ width: widths[c.key] || undefined }}
                  onClick={() => c.sortable !== false && toggleSort(c.key)} ref={dragRef}>
                  <span style={{ display: "inline-flex", alignItems: "center", gap: 4 }}>
                    {c.label}
                    {c.sortable !== false && (sort.key === c.key
                      ? (sort.dir === 1 ? <ArrowUp size={11} /> : <ArrowDown size={11} />)
                      : <ArrowUpDown size={11} style={{ opacity: 0.4 }} />)}
                  </span>
                  <span className="resizer" onMouseDown={(e) => startResize(e, c.key)} onClick={(e) => e.stopPropagation()} />
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {filtered.length ? filtered.map((row, i) => (
              <tr key={row.id || i} className={onRowClick ? "click" : ""}
                data-testid={`${testid}-row-${row.id || i}`}
                onClick={() => onRowClick && onRowClick(row)}>
                {columns.map((c) => (
                  <td key={c.key} className={c.mono ? "mono" : ""} style={{ width: widths[c.key] || undefined }}>
                    {c.render ? c.render(row) : row[c.key]}
                  </td>
                ))}
              </tr>
            )) : <tr><td colSpan={columns.length} className="empty">{emptyText}</td></tr>}
          </tbody>
        </table>
      </div>
      {cardRender && (
        <div className="dt-cards" data-testid={`${testid}-cards`}>
          {filtered.length ? filtered.map((row, i) => (
            <div key={row.id || i} className="dt-card" data-testid={`${testid}-card-${row.id || i}`}
              onClick={() => onRowClick && onRowClick(row)}>
              {cardRender(row)}
            </div>
          )) : <div className="empty">{emptyText}</div>}
        </div>
      )}
    </div>
  );
}
